/**
 * §14.8 screen 7 — Statistika.
 *
 * Every distribution the panel can defend, in one call to
 * `/admin/stats/breakdown`, plus the received/closed trend from
 * `/admin/stats/timeseries`.
 *
 * ===========================================================================
 * THE FOUR-BUCKET RULE (§14.3.4) IS THIS SCREEN'S REASON FOR EXISTING.
 * ===========================================================================
 *
 * `by_legal_type` always reports FOUR buckets, and `unclassified` is:
 *
 *   - never hidden,
 *   - never proportionally redistributed into the other three,
 *   - never excluded from the denominator.
 *
 * So the legal-type card here renders `unclassified` ALWAYS — including when it
 * is zero, where a chart library would normally drop the slice. A legal-type
 * pie that silently omits a third of the appeals is a false official report,
 * and this screen is the one a hokim reads numbers off. The card additionally
 * states the unclassified SHARE in words above the chart, because a slice a
 * reader has to measure by eye is a slice they will round down.
 *
 * `analytics.read`, so a `viewer` reaches it: counts and ratios only, no name,
 * no phone, no reference, no appeal text.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { LEGAL_TYPE_BUCKETS, MUROJAAT_STATUSES } from '@navoiy/domain';
import { ApiError, api, apiBaseUrl, errorKey, jsonBody, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import {
  BarChart,
  CLOSED_COLOR,
  Donut,
  RECEIVED_COLOR,
  STATUS_COLOR,
  Sparkline,
  type Slice,
} from '../components/charts/index.jsx';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatDate, formatPercent, fromDateInput } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  Select,
  Spinner,
  TextInput,
  WarningBanner,
} from '../ui/index.jsx';

type Breakdown = components['schemas']['AdminBreakdownResponse'];
type Timeseries = components['schemas']['AdminTimeseriesResponse'];
type Bucket = 'day' | 'week' | 'month';

interface Bounds {
  from: string;
  to: string;
}

const EMPTY_BOUNDS: Bounds = { from: '', to: '' };

/** Turns a `{key: count}` block into slices, keeping EVERY key including zeros. */
function slices(
  values: Record<string, number>,
  keys: readonly string[],
  label: (key: string) => string,
  color?: (key: string) => Slice['color'],
): Slice[] {
  return keys.map((key) => ({
    key,
    label: label(key),
    value: values[key] ?? 0,
    ...(color ? { color: color(key) } : {}),
  }));
}

export function StatsScreen(): React.JSX.Element {
  const { t } = useI18n();
  const { expire, allows } = useSession();

  const [bounds, setBounds] = useState<Bounds>(EMPTY_BOUNDS);
  const [applied, setApplied] = useState<Bounds>(EMPTY_BOUNDS);
  const [bucket, setBucket] = useState<Bucket>('day');
  const [breakdown, setBreakdown] = useState<Breakdown | null>(null);
  const [trend, setTrend] = useState<Timeseries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = fromDateInput(applied.from);
      const to = fromDateInput(applied.to, true);
      const range = { ...(from ? { from } : {}), ...(to ? { to } : {}) };

      const [breakdownResult, timeseriesResult] = await Promise.all([
        api().GET('/admin/stats/breakdown', { params: { query: range } }),
        api().GET('/admin/stats/timeseries', { params: { query: { bucket, ...range } } }),
      ]);
      setBreakdown(jsonBody(unwrap(breakdownResult)));
      setTrend(jsonBody(unwrap(timeseriesResult)));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        expire();
        return;
      }
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    } finally {
      setLoading(false);
    }
  }, [applied, bucket, expire, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const legal = breakdown?.by_legal_type;
  const legalTotal = legal
    ? legal.ariza + legal.shikoyat + legal.taklif + legal.unclassified
    : 0;
  const unclassifiedPct =
    legal && legalTotal > 0 ? Math.round((legal.unclassified / legalTotal) * 1000) / 10 : undefined;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-brand-900">{t('admin.stats.title')}</h1>
        {breakdown?.scoped ? <Badge tone="gold">{t('admin.scoped.label')}</Badge> : null}
      </div>

      <Card
        title={t('admin.stats.filters')}
        actions={
          <div className="flex items-center gap-2">
            <Button tone="secondary" onClick={() => setApplied(EMPTY_BOUNDS)}>
              {t('admin.inbox.filterReset')}
            </Button>
            <Button onClick={() => setApplied(bounds)}>{t('admin.common.apply')}</Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Field label={t('admin.inbox.filterFrom')}>
            <TextInput
              type="date"
              value={bounds.from}
              onChange={(event) => setBounds({ ...bounds, from: event.target.value })}
            />
          </Field>
          <Field label={t('admin.inbox.filterTo')}>
            <TextInput
              type="date"
              value={bounds.to}
              onChange={(event) => setBounds({ ...bounds, to: event.target.value })}
            />
          </Field>
          <Field label={t('admin.stats.bucket')}>
            <Select value={bucket} onChange={(event) => setBucket(event.target.value as Bucket)}>
              <option value="day">{t('admin.stats.bucketDay')}</option>
              <option value="week">{t('admin.stats.bucketWeek')}</option>
              <option value="month">{t('admin.stats.bucketMonth')}</option>
            </Select>
          </Field>
          <div className="flex items-end pb-2 text-xs text-ink-500">
            {breakdown?.from || breakdown?.to
              ? t('admin.mahallas.window', {
                  from: formatDate(breakdown.from),
                  to: formatDate(breakdown.to),
                })
              : t('admin.mahallas.windowAll')}
          </div>
        </div>
      </Card>

      <ErrorBanner message={error} />

      {loading && !breakdown ? <Spinner label={t('admin.common.loading')} /> : null}

      {breakdown && legal ? (
        <>
          {/* ---- the legal-type card, and the four-bucket rule ------------ */}
          <Card
            title={t('admin.stats.legalTypeTitle')}
            actions={
              allows('analytics.export') ? (
                <ExportLink name="breakdown" applied={applied} label={t('admin.reports.csv')} />
              ) : null
            }
          >
            <div className="flex flex-col gap-4">
              {/*
                §14.3.4 in words, above the chart. A reader who has to judge the
                unclassified slice by eye will round it down; a sentence cannot
                be rounded. It renders at every value, including 0 %, so its
                presence never itself carries information.
              */}
              <WarningBanner>
                {t('admin.stats.unclassifiedNotice', {
                  count: legal.unclassified,
                  percent: formatPercent(unclassifiedPct),
                })}
              </WarningBanner>

              <Donut
                label={t('admin.stats.legalTypeTitle')}
                slices={slices(legal as unknown as Record<string, number>, LEGAL_TYPE_BUCKETS, (key) =>
                  t(`admin.legalType.${key}`),
                )}
                centerValue={String(legalTotal)}
                centerLabel={t('admin.column.total')}
                emptyLabel={t('admin.chart.empty')}
              />

              {/*
                The same four numbers as text. The donut drops a zero-value
                slice from the ring for the obvious geometric reason; this list
                does not, so a bucket at zero is still visibly REPORTED as zero
                rather than missing.
              */}
              <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {LEGAL_TYPE_BUCKETS.map((key) => (
                  <li
                    key={key}
                    className="rounded-chip bg-surface-50 px-3 py-2 text-sm text-ink-700"
                  >
                    <span className="block text-xs uppercase tracking-label text-ink-500">
                      {t(`admin.legalType.${key}`)}
                    </span>
                    <span className="text-lg font-bold tabular-nums text-ink-900">
                      {(legal as unknown as Record<string, number>)[key] ?? 0}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card title={t('admin.stats.byStatus')}>
              <Donut
                label={t('admin.stats.byStatus')}
                slices={slices(
                  breakdown.by_status as unknown as Record<string, number>,
                  MUROJAAT_STATUSES,
                  (key) => t(`shared.murojaat.status.${key}`),
                  (key) => STATUS_COLOR[key],
                )}
                centerValue={String(
                  Object.values(breakdown.by_status as unknown as Record<string, number>).reduce(
                    (a, b) => a + b,
                    0,
                  ),
                )}
                centerLabel={t('admin.column.total')}
                emptyLabel={t('admin.chart.empty')}
              />
            </Card>

            <Card title={t('admin.stats.byChannel')}>
              <Donut
                label={t('admin.stats.byChannel')}
                slices={slices(
                  breakdown.by_channel as unknown as Record<string, number>,
                  Object.keys(breakdown.by_channel),
                  (key) => t(`admin.channel.${key}`),
                )}
                centerValue={String(
                  Object.values(breakdown.by_channel as unknown as Record<string, number>).reduce(
                    (a, b) => a + b,
                    0,
                  ),
                )}
                centerLabel={t('admin.column.total')}
                emptyLabel={t('admin.chart.empty')}
              />
            </Card>

            <Card title={t('admin.stats.byKind')}>
              <BarChart
                label={t('admin.stats.byKind')}
                items={slices(
                  breakdown.by_kind as unknown as Record<string, number>,
                  Object.keys(breakdown.by_kind),
                  (key) => t(`shared.murojaat.kind.${key}`),
                )}
                emptyLabel={t('admin.chart.empty')}
              />
            </Card>

            <Card title={t('admin.stats.byLocale')}>
              <BarChart
                label={t('admin.stats.byLocale')}
                items={slices(
                  breakdown.by_locale as unknown as Record<string, number>,
                  Object.keys(breakdown.by_locale),
                  (key) => key,
                )}
                emptyLabel={t('admin.chart.empty')}
              />
            </Card>
          </div>

          <Card title={t('admin.stats.trendTitle')}>
            {/*
              Two series on ONE shared scale — drawing them against independent
              axes would make a growing backlog look like a balance, which is
              the exact misreading this chart exists to prevent.
            */}
            <Sparkline
              label={t('admin.stats.trendTitle')}
              series={[
                {
                  key: 'received',
                  label: t('admin.stats.seriesReceived'),
                  points: (trend?.items ?? []).map((point) => point.received),
                  color: RECEIVED_COLOR,
                },
                {
                  key: 'closed',
                  label: t('admin.stats.seriesClosed'),
                  points: (trend?.items ?? []).map((point) => point.closed),
                  color: CLOSED_COLOR,
                },
              ]}
              emptyLabel={t('admin.chart.empty')}
            />
          </Card>
        </>
      ) : null}
    </div>
  );
}

/**
 * A plain `<a download>` rather than a fetch (§14.2 `format=csv`).
 *
 * The CSV branch is a browser download of an authenticated same-origin URL: the
 * session cookie rides along, the file lands in the download folder, and no
 * blob is assembled in the panel's memory. `analytics.export` is `admin`-only
 * and the server enforces it regardless of whether this link is rendered.
 */
export function ExportLink({
  name,
  applied,
  label,
  extraQuery = '',
}: {
  name: string;
  applied: { from: string; to: string };
  label: string;
  extraQuery?: string;
}): React.JSX.Element {
  const from = fromDateInput(applied.from);
  const to = fromDateInput(applied.to, true);
  const query = [
    'format=csv',
    ...(from ? [`from=${encodeURIComponent(from)}`] : []),
    ...(to ? [`to=${encodeURIComponent(to)}`] : []),
    ...(extraQuery ? [extraQuery] : []),
  ].join('&');
  return (
    <a
      href={`${apiBaseUrl()}/admin/stats/${name}?${query}`}
      className="inline-flex items-center rounded-btn border-1 border-line-300 bg-surface-0 px-4 py-2 text-sm font-semibold text-brand-800 hover:bg-surface-100"
    >
      {label}
    </a>
  );
}
